import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import React from 'react';
import { isMobile } from 'react-device-detect';
import { DownloadActions, DownloadState } from '../hooks/useDownloadManager';

/**
 * Props for the DownloadButtonComponent
 */
interface DownloadButtonComponentProps {
    /** Whether the component should display in sticky mode (compact layout) */
    isSticky?: boolean;
    /** Current state of the download manager */
    downloadState: DownloadState;
    /** Available actions from the download manager */
    downloadActions: DownloadActions;
    /** Array of event photos that can be selected for download */
    eventPhotos: any[];
}

/**
 * DownloadButtonComponent provides UI controls for managing photo downloads
 * Features:
 * - Select all photos checkbox (hidden in sticky mode)
 * - Download selected photos button with progress feedback
 * - Clear selection button
 * - Error display for failed downloads
 * - Shows selection count in sticky mode
 */
export const DownloadButtonComponent: React.FC<DownloadButtonComponentProps> = ({
    isSticky = false,
    downloadState,
    downloadActions,
    eventPhotos
}) => {
    // Destructure download state for easier access
    const {
        selectedImages,
        downloadButtonContent,
        allImagesAreSelectedForDownload,
        downloadPhotosZipInProgress,
        downloadJobStatus,
        downloadPhotosZipError
    } = downloadState;

    // Destructure download actions for easier access
    const {
        dowloadSelectedImages,
        clearSelectedImages,
        handleSelectAllForDownloadCheckBoxChanged
    } = downloadActions;

    // Don't render on mobile devices
    if (isMobile) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* Select all checkbox - only shown in non-sticky mode for space conservation */}
            {!isSticky && (
                <FormControlLabel
                    control={
                        <Checkbox
                            key={'all-images-select-all-checkbox'}
                            onChange={(event, checked) => handleSelectAllForDownloadCheckBoxChanged(checked, eventPhotos)}
                            checked={allImagesAreSelectedForDownload}
                        />
                    }
                    label="Markera alla bilder för nedladdning"
                />
            )}

            {/* Show download controls only when images are selected */}
            {selectedImages.length > 0 ? (
                <>
                    {/* Download button with dynamic content and progress feedback */}
                    <Button
                        variant='outlined'
                        onClick={() => dowloadSelectedImages()}
                        // Disable during download or while job is processing
                        disabled={downloadPhotosZipInProgress || (downloadJobStatus !== null && downloadJobStatus !== 'completed' && downloadJobStatus !== 'failed')}
                        sx={{
                            // Expand button width during zip creation to accommodate progress text
                            minWidth: downloadJobStatus === 'in-progress-making-zip' ? 400 : 'auto',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {downloadButtonContent}
                    </Button>

                    {/* Clear selection button */}
                    <Button
                        variant='outlined'
                        onClick={clearSelectedImages}
                        // Disable during download or while job is processing
                        disabled={downloadPhotosZipInProgress || (downloadJobStatus !== null && downloadJobStatus !== 'completed' && downloadJobStatus !== 'failed')}
                    >
                        Rensa markering
                    </Button>

                    {/* In sticky mode, show selection count instead of checkbox */}
                    {isSticky && (
                        <Typography variant="body2" color="text.secondary">
                            {selectedImages.length} bilder markerade
                        </Typography>
                    )}
                </>
            ) : (
                // Empty fragment when no images are selected
                <></>
            )}

            {/* Error message display */}
            {downloadPhotosZipError ? (
                <Typography color="error" variant="body2" sx={{ width: '100%' }}>
                    Det gick inte att ladda ned zip-filen. Försök igen senare.
                </Typography>
            ) : (
                // Empty fragment when no error
                <></>
            )}
        </Box>
    );
};
